/*
 * The masonry of the tiles layouts (concepts/frontend-design.md, "Lists"): the tiles of an
 * event and of the events, the same layouts of the event module, the events module and of an
 * event tag. The tiles stand in a CSS grid of columns (tiles-list.less: as many as
 * --eg-tile-min allows, or the --eg-columns of the breakpoint editor) and keep their own
 * height; they are read row-wise as in 6.x - images 1, 2, 3 ... from left to right, every
 * tile in the shortest column - which is also the order of the lightbox.
 *
 * The script measures every tile and lets it span as many fine rows of the grid as its
 * height and the gap below it take (--eg-span, rows of --eg-masonry-unit); the auto-placement
 * of the grid then puts each tile into the first free place in reading order, which is the
 * top of the shortest column, the leftmost of equal ones - the rule of 6.x. Nothing is
 * positioned absolutely, nothing floats, and without the script the tiles stand in rows of
 * the grid in the same order. It measures a tile once, when it arrives, and takes every later
 * height from a ResizeObserver (a window, the column count, a font which arrives), which also
 * says when the width of the list changes; the gap and the columns are read again only then.
 * The one tile it measures again is the last tile of a list the browser is still parsing: the
 * parser may stand inside it, and the observer would report the rest of it only after the frame
 * which shows it (calculate()).
 * Until the review of #1894 every pass measured every tile, also while the page was parsed
 * (1,000 and more measurements of a tile on a slow phone). Until 7.0.0 the script positioned
 * every tile absolutely and hid the tiles until then.
 */

/** the most rows a grid gets: Firefox stops at 10,000 lines */
const MAX_ROWS = 9000;

const instances = new WeakMap();

/** on the root element of a page whose tiles this script stacks (tiles-list.less) */
const ANNOUNCED = 'eg-masonry-script';

export default class TilesCollection {

    /**
     * @param {HTMLElement} container the grid of the tiles (.eventgallery-tiles)
     */
    constructor(container) {
        this.container = container;
        this.spans = new Map();
        /** @type {Map<HTMLElement, number>} the height of every tile measured so far */
        this.heights = new Map();
        this.observed = new Set();
        this.unit = 0;
        this.frame = 0;
        this.observer = null;
        /** @type {{gap: number, columns: number}|null} read again when the width of the list changes */
        this.grid = null;
        this.width = -1;
        /** @type {HTMLElement|null} the last tile of the pass before, measured while the page was parsed */
        this.open = null;
    }

    /**
     * Says on the root element that the tiles of this page are stacked by the script, before
     * the browser parses the first of them: the stylesheet then keeps a tile hidden until it has
     * its span (tiles-list.less). Without the script the tiles stand in the rows of the grid.
     *
     * @param {Document} [doc]
     */
    static announce(doc = document) {
        doc.documentElement.classList.add(ANNOUNCED);
    }

    /**
     * Stacks every list of tiles below root: a new one at once, one the browser is still
     * parsing (whileParsing) with the tiles it has so far - the tiles placed stay where they
     * are when more arrive.
     *
     * @param {Document|HTMLElement} root
     * @returns {TilesCollection[]} the lists bound now for the first time
     */
    static bind(root = document) {
        const bound = [];
        root.querySelectorAll('.eventgallery-tiles-list .eventgallery-tiles').forEach((container) => {
            const known = instances.get(container);
            if (known) {
                if (known.open || known.tiles().length !== known.heights.size) {
                    known.calculate();
                    known.observeTiles();
                }
                return;
            }
            const collection = new TilesCollection(container);
            instances.set(container, collection);
            collection.start();
            bound.push(collection);
        });
        return bound;
    }

    start() {
        this.calculate();
        this.observe();
    }

    /**
     * @returns {HTMLElement[]} the tiles of the list
     */
    tiles() {
        return Array.from(this.container.children).filter((element) => element.classList.contains('eventgallery-tile'));
    }

    /**
     * @param {HTMLElement} container
     * @returns {number} the columns of the grid
     */
    static columnCount(container) {
        const columns = window.getComputedStyle(container).gridTemplateColumns || '';
        const count = columns === 'none' ? 0 : columns.split(/\s+/).filter((track) => track && !track.startsWith('[')).length;
        return Math.max(1, count);
    }

    /**
     * The height of a fine row: 1 px, unless the list is so long that the grid would get more
     * rows than a browser allows.
     *
     * @param {number[]} heights the heights of the tiles
     * @param {number} gap the gap below a tile
     * @param {number} columns
     * @returns {number} in px
     */
    static unitFor(heights, gap, columns) {
        if (heights.length === 0) {
            return 1;
        }
        const total = heights.reduce((sum, height) => sum + height + gap, 0);
        const tallest = Math.max(...heights) + gap;
        return Math.max(1, Math.ceil((total / columns + tallest) / MAX_ROWS));
    }

    /**
     * @param {number} height the height of a tile
     * @param {number} gap
     * @param {number} unit
     * @returns {number} how many fine rows the tile and the gap below it take
     */
    static spanFor(height, gap, unit) {
        return Math.max(1, Math.ceil((height + gap) / unit));
    }

    /**
     * Measures the tiles which have no height yet first and then writes the spans, so the page
     * is laid out once.
     *
     * While the browser parses the page, the last tile of the list is measured again in the
     * next pass: the parser may stand inside it - a document arrives in pieces, on a slow line
     * or from a busy server -, and then it was measured with a part of its content, or empty.
     * The observer reports its real height only after the layout of the frame which shows it,
     * so the tiles which arrived with the rest of it were stacked for the height of the part,
     * painted, and moved in the frame after (a layout shift of 0.2 to 0.4 for a document in
     * pieces of 16 to 30 kB; decisions/frontend-lists-and-layouts.md).
     */
    calculate() {
        const tiles = this.tiles();
        if (tiles.length === 0) {
            return;
        }
        if (this.grid === null) {
            this.grid = {
                gap: parseFloat(window.getComputedStyle(this.container).columnGap) || 0,
                columns: TilesCollection.columnCount(this.container),
            };
        }
        tiles.forEach((tile) => {
            if (!this.heights.has(tile) || tile === this.open) {
                this.heights.set(tile, tile.getBoundingClientRect().height);
            }
        });
        this.open = this.container.ownerDocument.readyState === 'loading' ? tiles[tiles.length - 1] : null;
        const heights = tiles.map((tile) => this.heights.get(tile));
        if (heights.every((height) => height === 0)) {
            // a list in a hidden tab: it is measured when it is shown
            return;
        }
        const gap = this.grid.gap;
        const unit = TilesCollection.unitFor(heights, gap, this.grid.columns);

        if (unit !== this.unit) {
            this.container.style.setProperty('--eg-masonry-unit', unit + 'px');
            this.unit = unit;
        }
        tiles.forEach((tile, index) => {
            const span = TilesCollection.spanFor(heights[index], gap, unit);
            if (this.spans.get(tile) !== span) {
                tile.style.setProperty('--eg-span', String(span));
                this.spans.set(tile, span);
            }
        });
        this.container.classList.add('eg-masonry');
    }

    /**
     * Stacks the tiles again when the width of the list or the height of a tile changes, in
     * the next frame: the spans change the height of the list, which the observer would
     * otherwise report within the same frame as a loop. The observer says the new height of a
     * tile itself, so no tile is measured again; a new width of the list reads the gap and the
     * columns again.
     */
    observe() {
        if (typeof ResizeObserver === 'undefined') {
            // without an observer every tile is measured again
            const again = () => {
                this.heights.clear();
                this.grid = null;
                this.schedule();
            };
            window.addEventListener('resize', again);
            window.addEventListener('load', again);
            return;
        }
        this.observer = new ResizeObserver((entries) => {
            let changed = false;
            entries.forEach((entry) => {
                if (entry.target === this.container) {
                    if (entry.contentRect.width !== this.width) {
                        this.width = entry.contentRect.width;
                        this.grid = null;
                        changed = true;
                    }
                    return;
                }
                const size = entry.borderBoxSize && entry.borderBoxSize[0];
                const height = size ? size.blockSize : entry.target.getBoundingClientRect().height;
                if (this.heights.get(entry.target) !== height) {
                    this.heights.set(entry.target, height);
                    changed = true;
                }
            });
            if (changed) {
                this.schedule();
            }
        });
        this.observer.observe(this.container);
        this.observeTiles();
    }

    /**
     * Watches the tiles which are not watched yet: a tile changes its height when its text
     * wraps anew, a font arrives or the browser has parsed the rest of it.
     */
    observeTiles() {
        if (!this.observer) {
            return;
        }
        this.tiles().forEach((tile) => {
            if (!this.observed.has(tile)) {
                this.observed.add(tile);
                this.observer.observe(tile);
            }
        });
    }

    schedule() {
        if (this.frame) {
            return;
        }
        this.frame = window.requestAnimationFrame(() => {
            this.frame = 0;
            this.calculate();
        });
    }
}
